import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid2,
  TextField,
  Button,
  Chip,
  Stack,
  Modal,
  Alert,
  Snackbar,
  Divider,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useForm, Controller, watch } from "react-hook-form";
import { submitFormData } from "../../actions/promptStoreAction";
import { useNavigate, useParams } from "react-router-dom";
import ModalBox from "../../themes/overrides/ModalBox";
import AiModelSelector from "./AiModelSelector";
import { generateAIOutput } from "./AiGenerator";

const CreatePromptForm = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const { register, handleSubmit, control, reset, watch } = useForm();
  const [variables, setVariables] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [selectedModel, setSelectedModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [testOutput, setTestOutput] = useState("");
  const promptTemplate = watch("promptTemplate", "");
  const systemInstruction = watch("systemInstruction");
  const [output, setOutput] = useState("");

  // Auto detect variables in the template
  useEffect(() => {
    const matches = promptTemplate.match(/{{(.*?)}}/g);
    if (matches) {
      const cleaned = matches.map((m) => m.replace(/{{|}}/g, "").trim());
      setVariables([...new Set(cleaned)]);
    } else {
      setVariables([]);
    }
  }, [promptTemplate]);

   const handleModelSelect = (model) => {
    setSelectedModel(model);
  };

  const handleVariableChange = (key, value) => {
    setVariableValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleTestRun = async () => {
    console.log(selectedModel);
    try {
      if (!selectedModel) {
        setSeverity("error");
        setErrorMessage("Please select an AI model first.");
        setOpenSnackbar(true);
        return;
      }

      if (!promptTemplate) {
        setSeverity("error");
        setErrorMessage("Please enter a prompt template first.");
        setOpenSnackbar(true);
        return;
      }

      setTestLoading(true);

      // Replace variables with values
      let filledPrompt = promptTemplate;
      variables.forEach((v) => {
        const value = variableValues[v] || "";
        filledPrompt = filledPrompt.replaceAll(`{{${v}}}`, value);
      });

      filledPrompt = systemInstruction + ". " + filledPrompt;


      console.log(filledPrompt);
      const result = await generateAIOutput(selectedModel, filledPrompt, (streamChunk) => {
        setOutput(streamChunk); // updates live
      });

      console.log("Final Output:", result);

    } catch (error) {
      console.error(error);
      setTestOutput("Error during generation.");
    } finally {
      setTestLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const formData = { workspaceId, ...data, variables };
      const res = await dispatch(submitFormData(formData));

      if (onSuccess) onSuccess(res);
      if (onClose) onClose();

      if (res?.status === "success") {
        navigate(`/${workspaceId}/prompt-library`);
        setSuccessMessage(res.message);
        setOpenSnackbar(true);
        setSeverity("success");
        reset();
        onClose();
      } else {
        setErrorMessage(res.message);
        setOpenSnackbar(true);
        setSeverity("error");
      }
    } catch (error) {
      setErrorMessage(error.message || "An error occurred.");
      setOpenSnackbar(true);
      setSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);
  const handleClose = () => {
    reset();
    onClose();
    setOutput("");

  };

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <ModalBox
          sx={{
            width: { xs: "95%", sm: "600px", md: "700px" },
            maxHeight: { xs: "95vh", sm: "90vh" },
          }}
        >
          <Typography variant="h2" sx={{ marginBottom: 2 }}>
            Create Prompt
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Basic Details */}
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <TextField
                  label="Prompt Name"
                  fullWidth
                  required
                  {...register("promptName", { required: true })}
                  placeholder="e.g., Blog Post Generator"
                />
              </Grid2>

              <Grid2 size={12}>
                <TextField
                  label="Category"
                  fullWidth
                  {...register("category")}
                  placeholder="e.g., Marketing, Coding, Creative"
                />
              </Grid2>
            </Grid2>

            <Box mt={3}>
              <TextField
                label="System Instruction (LLM Persona)"
                fullWidth
                multiline
                rows={2}
                {...register("systemInstruction")}
                placeholder="e.g., Act as a senior technical writer..."
              />
            </Box>

            <Box mt={3}>
              <TextField
                label="Prompt Template Text"
                fullWidth
                multiline
                rows={6}
                {...register("promptTemplate")}
                placeholder="Write a blog post about {{topic}} for a {{target_audience}}."
              />
            </Box>

            {/* Auto Detected Variables */}
            <Box mt={4}>
              <Typography variant="subtitle1" fontWeight={500} mb={1}>
                Detected Variables
              </Typography>
              {variables.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" spacing={1}>
                  {variables.map((v, i) => (
                    <Chip key={i} label={v} color="secondary" variant="outlined" />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No variables detected in your template.
                </Typography>
              )}
            </Box>

            {/* DIY Run Section */}
            <Box mt={4}  variant="outlined" sx={{p:2,  backgroundColor: "#e6f7fdff"}}>
              {/* <Divider sx={{ mb: 2 }} /> */}
              <Typography variant="subtitle1" fontWeight={500} mb={2}>
                DIY Test Run
              </Typography>

            <AiModelSelector
                    selectedModel={selectedModel}
                    onModelSelect={handleModelSelect}
                  />
              {variables.length > 0 && (
                <Box mt={2}>
                  {variables.map((v) => (
                    <TextField
                      key={v}
                      label={`Value for ${v}`}
                      fullWidth
                      margin="dense"
                      value={variableValues[v] || ""}
                      onChange={(e) => handleVariableChange(v, e.target.value)}
                    />
                  ))}
                </Box>
              )}

              <Box mt={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleTestRun}
                  disabled={testLoading}
                >
                  {testLoading ? <CircularProgress size={24} color="inherit" /> : "Run Test"}
                </Button>
              </Box>
              {output && (
                <Paper
                  variant="outlined"
                  sx={{ mt: 3, p: 2, backgroundColor: "#f9f9f9", whiteSpace: "pre-wrap" }}
                >
                  <Typography variant="subtitle2" mb={1}>
                    Test Output:
                  </Typography>
                  <Typography variant="body2">{output}</Typography>
                </Paper>
              )}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={2}
              mt={5}
            >
              <Button variant="outlined" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? "Creating..." : "Create Prompt"}
              </Button>
            </Stack>
          </Box>
        </ModalBox>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {severity === "success" ? successMessage : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreatePromptForm;
